/**
 * Live Flight Service using OpenSky Network API
 * Provides real-time flight data for interactive mapping
 */

export interface LiveFlight {
  icao24: string;        // Unique aircraft identifier
  callsign: string;      // Flight number/callsign
  originCountry: string; // Country of origin
  timePosition: number;  // Last position update timestamp
  lastContact: number;   // Last contact timestamp
  longitude: number;     // Current longitude
  latitude: number;      // Current latitude
  baroAltitude: number;  // Barometric altitude in meters
  onGround: boolean;     // Whether aircraft is on ground
  velocity: number;      // Ground speed in m/s
  trueTrack: number;     // Track angle in degrees
  verticalRate: number;  // Vertical rate in m/s
  sensors: number[];     // Sensor array
  geoAltitude: number;   // Geometric altitude in meters
  squawk: string;        // Transponder code
  spi: boolean;          // Special position identification
  positionSource: number; // Position source
}

export interface FlightMapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface ProcessedFlight {
  id: string;
  callsign: string;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  heading: number;
  airline: string;
  country: string;
  isOnGround: boolean;
  lastSeen: Date;
  path?: [number, number][]; // Array of [lat, lng] coordinates for flight path
  origin?: string; // Departure airport/city
  destination?: string; // Arrival airport/city
  flightType?: string; // Domestic or International
}

class LiveFlightService {
  private readonly BASE_URL = 'https://opensky-network.org/api';
  private readonly CACHE_DURATION = 30000; // 30 seconds
  private cachedFlights: ProcessedFlight[] = [];
  private flightHistory: Map<string, [number, number][]> = new Map(); // Track flight paths
  private lastFetchTime: number = 0;

  /**
   * Get all live flights globally (cached for performance)
   */
  async getAllFlights(): Promise<ProcessedFlight[]> {
    const now = Date.now();
    
    // Return cached data if still fresh
    if (this.cachedFlights.length > 0 && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return this.cachedFlights;
    }

    try {
      console.log('🛩️ Fetching live flight data from OpenSky Network...');
      const response = await fetch(`${this.BASE_URL}/states/all`);
      
      if (!response.ok) {
        throw new Error(`OpenSky API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.states || !Array.isArray(data.states)) {
        throw new Error('Invalid response format from OpenSky API');
      }

      // Process raw flight data
      this.cachedFlights = this.processFlightData(data.states);
      this.lastFetchTime = now;
      
      console.log(`✅ Loaded ${this.cachedFlights.length} live flights`);
      return this.cachedFlights;
    } catch (error) {
      console.error('Error fetching live flights:', error);
      
      // Return demo data if API fails
      return this.getDemoFlights();
    }
  }

  /**
   * Get flights within specific geographic bounds
   */
  async getFlightsInBounds(bounds: FlightMapBounds): Promise<ProcessedFlight[]> {
    try {
      const response = await fetch(
        `${this.BASE_URL}/states/all?lamin=${bounds.south}&lamax=${bounds.north}&lomin=${bounds.west}&lomax=${bounds.east}`
      );
      
      if (!response.ok) {
        throw new Error(`OpenSky API error: ${response.status}`);
      }

      const data = await response.json();
      return this.processFlightData(data.states || []);
    } catch (error) {
      console.error('Error fetching bounded flights:', error);
      return this.getDemoFlights().filter(flight => 
        flight.latitude >= bounds.south && flight.latitude <= bounds.north &&
        flight.longitude >= bounds.west && flight.longitude <= bounds.east
      );
    }
  }

  /**
   * Process raw OpenSky data into our format
   */
  private processFlightData(states: any[]): ProcessedFlight[] {
    return states
      .filter(state => {
        // Filter out invalid/incomplete data
        return state[5] && state[6] && // Has lat/lng
               state[5] !== null && state[6] !== null &&
               !isNaN(state[5]) && !isNaN(state[6]) &&
               Math.abs(state[5]) <= 90 && Math.abs(state[6]) <= 180; // Valid coordinates
      })
      .map(state => {
        const flightId = state[0] || 'unknown';
        const currentPosition: [number, number] = [state[6], state[5]];
        
        // Update flight path history
        if (!this.flightHistory.has(flightId)) {
          this.flightHistory.set(flightId, []);
        }
        
        const history = this.flightHistory.get(flightId)!;
        
        // Only add new position if it's significantly different from last position
        const lastPosition = history[history.length - 1];
        if (!lastPosition || 
            Math.abs(lastPosition[0] - currentPosition[0]) > 0.001 || 
            Math.abs(lastPosition[1] - currentPosition[1]) > 0.001) {
          history.push(currentPosition);
          
          // Limit path length to last 100 positions for better visibility
          if (history.length > 100) {
            history.shift();
          }
        }

        // Generate realistic origin/destination based on location and airline
        const generateRoute = (callsign: string, icao: string, lat: number, lng: number, airline: string) => {
          // Indian airports for domestic flights
          const indianAirports = [
            'DEL (New Delhi)', 'BOM (Mumbai)', 'BLR (Bangalore)', 'MAA (Chennai)',
            'HYD (Hyderabad)', 'CCU (Kolkata)', 'AMD (Ahmedabad)', 'PNQ (Pune)',
            'GOI (Goa)', 'COK (Kochi)', 'TRV (Thiruvananthapuram)', 'JAI (Jaipur)',
            'LKO (Lucknow)', 'PAT (Patna)', 'GAU (Guwahati)', 'IXR (Ranchi)',
            'BBI (Bhubaneswar)', 'VNS (Varanasi)', 'IXC (Chandigarh)', 'SXR (Srinagar)',
            'ATQ (Amritsar)', 'IXL (Leh)', 'IXA (Agartala)', 'IXS (Silchar)',
            'VTZ (Visakhapatnam)', 'TIR (Tirupati)', 'CJB (Coimbatore)', 'TRZ (Trichy)',
            'IXM (Madurai)', 'IXE (Mangalore)', 'HBX (Hubli)', 'BEL (Belgaum)'
          ];

          // International airports
          const internationalAirports = [
            'LHR (London)', 'CDG (Paris)', 'FRA (Frankfurt)', 'AMS (Amsterdam)',
            'DXB (Dubai)', 'DOH (Doha)', 'AUH (Abu Dhabi)', 'SIN (Singapore)',
            'BKK (Bangkok)', 'KUL (Kuala Lumpur)', 'HKG (Hong Kong)', 'NRT (Tokyo)',
            'ICN (Seoul)', 'PEK (Beijing)', 'SYD (Sydney)', 'MEL (Melbourne)',
            'JFK (New York)', 'LAX (Los Angeles)', 'ORD (Chicago)', 'YYZ (Toronto)',
            'LGW (London Gatwick)', 'ZUR (Zurich)', 'VIE (Vienna)', 'CPH (Copenhagen)',
            'ARN (Stockholm)', 'FCO (Rome)', 'MAD (Madrid)', 'BCN (Barcelona)'
          ];

          // Check if flight is likely domestic (within India boundaries approximately)
          const isInIndia = lat >= 6 && lat <= 37 && lng >= 68 && lng <= 97;
          
          // Check for Indian airlines
          const indianAirlines = ['Air India', 'IndiGo', 'SpiceJet', 'Vistara', 'GoAir', 'AirAsia India'];
          const isIndianAirline = indianAirlines.some(ia => airline.toLowerCase().includes(ia.toLowerCase()));

          // Determine if it's a domestic flight:
          // - Must be an Indian airline AND flying within India
          // - Foreign airlines operating in India are still considered international
          const isDomesticFlight = isInIndia && isIndianAirline;

          const airports = isDomesticFlight ? indianAirports : 
                          (isInIndia ? [...indianAirports, ...internationalAirports] : internationalAirports);
          
          const hash = (callsign + icao).split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
          }, 0);
          
          const originIndex = Math.abs(hash) % airports.length;
          const destIndex = Math.abs(hash * 7) % airports.length;
          
          return {
            origin: airports[originIndex],
            destination: airports[destIndex !== originIndex ? destIndex : (destIndex + 1) % airports.length],
            flightType: isDomesticFlight ? 'Domestic' : 'International'
          };
        };

        const route = generateRoute((state[1] || '').trim() || state[0], flightId, state[6], state[5], this.extractAirline(state[1]));

        return {
          id: flightId,
          callsign: (state[1] || '').trim() || 'N/A',
          latitude: state[6],
          longitude: state[5],
          altitude: Math.round((state[7] || 0) * 3.28084), // Convert meters to feet
          speed: Math.round((state[9] || 0) * 1.94384), // Convert m/s to knots
          heading: state[10] || 0,
          airline: this.extractAirline(state[1]),
          country: state[2] || 'Unknown',
          isOnGround: state[8] || false,
          lastSeen: new Date((state[3] || Date.now() / 1000) * 1000),
          path: history.length >= 1 ? [...history] : [currentPosition], // Always provide path
          origin: route.origin,
          destination: route.destination,
          flightType: route.flightType
        };
      })
      .slice(0, 1000); // Limit to prevent browser overload
  }

  /**
   * Extract airline from callsign
   */
  private extractAirline(callsign: string): string {
    if (!callsign || callsign.trim() === '') return 'Unknown';
    
    const trimmed = callsign.trim();
    
    const airlineMap: { [key: string]: string } = {
      // Indian Airlines
      'AI': 'Air India',
      'UK': 'Vistara',
      '6E': 'IndiGo',
      'SG': 'SpiceJet',
      'G8': 'GoAir',
      'I5': 'AirAsia India',
      'IX': 'Air India Express',
      'DN': 'Alliance Air',
      '9W': 'Jet Airways',
      // International Airlines
      'EK': 'Emirates',
      'UAE': 'Emirates', // Emirates also uses UAE prefix
      'QR': 'Qatar Airways',
      'EY': 'Etihad Airways',
      'BA': 'British Airways',
      'LH': 'Lufthansa',
      'AF': 'Air France',
      'KL': 'KLM',
      'UA': 'United',
      'DL': 'Delta',
      'AA': 'American',
      'QF': 'Qantas',
      'SQ': 'Singapore Airlines',
      'CX': 'Cathay Pacific',
      'TG': 'Thai Airways',
      'MH': 'Malaysia Airlines',
    };

    // Try to match airline codes (check longer codes first for better accuracy)
    const sortedCodes = Object.keys(airlineMap).sort((a, b) => b.length - a.length);
    
    for (const code of sortedCodes) {
      if (trimmed.toUpperCase().startsWith(code.toUpperCase())) {
        return airlineMap[code];
      }
    }

    // If no match found, return the first 2-3 characters as fallback
    const fallback = trimmed.substring(0, Math.min(3, trimmed.length)).toUpperCase();
    return fallback;
  }

  /**
   * Get demo flights for fallback when API is unavailable
   */
  private getDemoFlights(): ProcessedFlight[] {
    return [
      {
        id: 'demo-1',
        callsign: 'AI131',
        latitude: 28.5665,
        longitude: 77.1031,
        altitude: 35000,
        speed: 480,
        heading: 90,
        airline: 'Air India',
        country: 'India',
        isOnGround: false,
        lastSeen: new Date(),
        path: [
          [28.5565, 77.0831],
          [28.5615, 77.0931],
          [28.5665, 77.1031]
        ]
      },
      {
        id: 'demo-2',
        callsign: 'UK955',
        latitude: 19.0896,
        longitude: 72.8656,
        altitude: 28000,
        speed: 420,
        heading: 270,
        airline: 'Vistara',
        country: 'India',
        isOnGround: false,
        lastSeen: new Date(),
        path: [
          [19.0996, 72.8856],
          [19.0946, 72.8756],
          [19.0896, 72.8656]
        ]
      },
      {
        id: 'demo-3',
        callsign: '6E2156',
        latitude: 13.1986,
        longitude: 77.7066,
        altitude: 0,
        speed: 0,
        heading: 180,
        airline: 'IndiGo',
        country: 'India',
        isOnGround: true,
        lastSeen: new Date(),
        path: [
          [13.1996, 77.7056],
          [13.1991, 77.7061],
          [13.1986, 77.7066]
        ]
      },
      {
        id: 'demo-4',
        callsign: 'EK432',
        latitude: 25.2532,
        longitude: 55.3657,
        altitude: 42000,
        speed: 520,
        heading: 135,
        airline: 'Emirates',
        country: 'United Arab Emirates',
        isOnGround: false,
        lastSeen: new Date(),
        path: [
          [25.2332, 55.3457],
          [25.2432, 55.3557],
          [25.2532, 55.3657]
        ]
      }
    ];
  }

  /**
   * Get flight statistics
   */
  getFlightStats(flights: ProcessedFlight[]) {
    const total = flights.length;
    const airborne = flights.filter(f => !f.isOnGround).length;
    const onGround = total - airborne;
    
    const airlines = flights.reduce((acc, flight) => {
      acc[flight.airline] = (acc[flight.airline] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    const topAirlines = Object.entries(airlines)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([airline, count]) => ({ airline, count }));

    return {
      total,
      airborne,
      onGround,
      topAirlines,
      averageAltitude: Math.round(
        flights.filter(f => !f.isOnGround).reduce((sum, f) => sum + f.altitude, 0) / airborne || 0
      ),
      averageSpeed: Math.round(
        flights.filter(f => !f.isOnGround).reduce((sum, f) => sum + f.speed, 0) / airborne || 0
      )
    };
  }
}

export const liveFlightService = new LiveFlightService();
export default liveFlightService;